import React from 'react';
import useOrganizationClientRecordInvoiceItems from '../../../hooks/api/useOrganizationClientRecordInvoiceItems';
import RichTable from '../../v2BaseComponents/RichTable';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import { TableConfig } from '../../v2BaseComponents/RichTable/types';
import { APITypes } from '../../../primary-record-types';
import { getIntl } from '../../../utils/intl';
import _truncate from 'lodash/truncate';
import getDateString from '../../../utils/getDateString';
import { useRichTableState } from '../../v2BaseComponents/RichTable/useRichTableState';
import EmptyStateHint from '../EmptyStateHint';
import Stack from '../../v2BaseComponents/Stack';
import Button from '../../v2BaseComponents/Button';
import { centsToDollarsConversion } from '../../../utils/centsToDollarsConversion';
import Text from '../../v2BaseComponents/Text';
import BillingContactDetailsHeader from './BillingContactDetailsHeader';
import { mainContentPadding } from '../../v2BaseComponents/MainContentContainer';
import Divider from '../../v2BaseComponents/Divider';

const intl = getIntl('client-invoice-items-list', {
    description: 'Description',
    amount: 'Amount',
    period: 'Period',
    addLineItem: 'Add Line Item',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    subTotal: 'Subtotal: {subtotal}',
});

export type InvoiceItemsListOwnProps = {
    clientRecordId: string;
    onAddInvoiceLineItem: () => void;
    onEditInvoiceLineItem: (invoiceItemId: string) => void;
    isInSideDrawer: boolean;
    onEditBillingDetails: () => void;
};

export type InvoiceItemsListProps = InvoiceItemsListOwnProps;

type GetElementType<T extends any[]> = T extends (infer U)[] ? U : never;
type TableItem = GetElementType<APITypes.organizationService.ListOrganizationClientRecordInvoiceItemsResponse['items']>;

const InvoiceItemsList: React.FC<InvoiceItemsListProps> = ({
    clientRecordId,
    onAddInvoiceLineItem,
    onEditInvoiceLineItem,
    isInSideDrawer,
    onEditBillingDetails,
}) => {
    const [tableState, setTableState] = useRichTableState();
    const { isLoadingInvoiceItems, invoiceItems, deleteInvoiceItems, isDeletingInvoiceItems } =
        useOrganizationClientRecordInvoiceItems({
            clientRecordId: clientRecordId,
        });

    const config: TableConfig<TableItem> = {
        columns: [
            {
                label: intl('description'),
                columnKey: 'description',
                render: (item) => _truncate(item.description, { length: 20 }),
            },
            {
                label: intl('period'),
                columnKey: 'period',
                render: (item) => {
                    if (!item.period) return '';

                    if (item.period.start === item.period.end) {
                        return getDateString(item.period.start, 'date');
                    } else {
                        return `${getDateString(item.period.start, 'date')} - ${getDateString(
                            item.period.end,
                            'date',
                        )}`;
                    }
                },
            },
            {
                label: 'Amount',
                columnKey: 'amount',
                render: (item) => {
                    if (item.amountDetails.amountType === 'fixed') {
                        return `$${centsToDollarsConversion.convertCentsToDollars(item.amountDetails.amount)}`;
                    } else {
                        return `${item.amountDetails.quantity} x $${centsToDollarsConversion.convertCentsToDollars(
                            item.amountDetails.unitAmount,
                        )} = $${centsToDollarsConversion.convertCentsToDollars(
                            item.amountDetails.quantity * item.amountDetails.unitAmount,
                        )}`;
                    }
                },
            },
        ],
        getRowKey: (item) => item.invoiceItemId,
    };

    return (
        <AnimateLoadedContent isLoading={isLoadingInvoiceItems || isDeletingInvoiceItems}>
            {() => (
                <Stack vertical fullWidth fullHeight space={4}>
                    <Stack vertical fullWidth style={{ padding: mainContentPadding, paddingTop: 2, paddingBottom: 2 }}>
                        <BillingContactDetailsHeader onEdit={onEditBillingDetails} clientRecordId={clientRecordId} />
                    </Stack>
                    <Divider horizontal />
                    {!invoiceItems?.items?.length ? (
                        <EmptyStateHint
                            variant="invoice-line-items"
                            renderSubContent={() => (
                                <Stack horizontal fullWidth justify="center">
                                    <Button
                                        size="lg"
                                        leftIcon="Plus"
                                        title={intl('addLineItem')}
                                        onPress={onAddInvoiceLineItem}
                                        testID="add-line-item-button"
                                    />
                                </Stack>
                            )}
                        />
                    ) : (
                        <RichTable
                            data={invoiceItems?.items || []}
                            state={tableState}
                            onStateChange={setTableState}
                            minWidth="100%"
                            noPaper={isInSideDrawer}
                            noPaperPadding
                            config={config}
                            allowRowSelection
                            headerRight={
                                invoiceItems?.subtotal ? (
                                    <Text variant="caption-2">
                                        {intl('subTotal', {
                                            subtotal: `$${centsToDollarsConversion.convertCentsToDollars(
                                                invoiceItems.subtotal,
                                            )}`,
                                        })}
                                    </Text>
                                ) : undefined
                            }
                            actions={[
                                {
                                    title: intl('add'),
                                    icon: 'PlusCircle',
                                    onPress: onAddInvoiceLineItem,
                                    shouldShow: () => tableState.selectedRowKeys.length === 0,
                                    testID: 'add-line-item-button',
                                },
                                {
                                    title: intl('edit'),
                                    icon: 'Edit',
                                    onPress: () => {
                                        if (tableState.selectedRowKeys.length === 1) {
                                            onEditInvoiceLineItem(tableState.selectedRowKeys[0]);
                                        }
                                    },
                                    shouldShow: () => tableState.selectedRowKeys.length === 1,
                                    testID: 'edit-line-item-button',
                                },
                                {
                                    title: intl('delete'),
                                    icon: 'Trash2',
                                    onPress: () => deleteInvoiceItems(tableState.selectedRowKeys),
                                    shouldShow: () => tableState.selectedRowKeys.length > 0,
                                    testID: 'delete-line-item-button',
                                },
                            ]}
                        />
                    )}
                </Stack>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(InvoiceItemsList);
